/**
 * EstimatedRates virtual views: seven per-method, ancestry-enriched surfaces
 * fronting the ~45-column `EstimatedRates_String_Agent` source
 * (estimated production rates per analysis method, string-grain, StringContext).
 *
 * The source carries the per-string estimated rates of every analysis method
 * (priority, system, choke, IPR, VLP, curve, data) as a single wide row keyed
 * by the `wellString` producing-string relation. Each measure column is a
 * method-prefixed standard-conditions phase rate or flowing bottom-hole
 * pressure (`<method>OilRateStd`, …, `<method>Fbhp`); the system/choke analyses
 * additionally carry a `Status` column and priority carries a `Method`
 * selector. Three un-prefixed reservoir-pressure context columns are shared
 * across every method.
 *
 * Each view here slices the wide row to one analysis method, de-prefixes the
 * method's measure family to canonical names via the shared shapes, keeps the
 * un-prefixed estimation context once, and enriches each row with its well /
 * field / reservoir ancestry resolved through the FlatHierarchy registry.
 *
 * The de-prefix axis is the analysis METHOD. The method prefixes carry the
 * `Analysis` segment for every method EXCEPT priority — `systemAnalysis*`,
 * `chokeAnalysis*`, `iprAnalysis*`, `vlpAnalysis*`, `curveAnalysis*`,
 * `dataAnalysis*`, but bare `priority*` — so the prefixes are authored verbatim
 * (NOT the bare method name) to keep `prefix + suffix === source column`.
 *
 * Built per session (not module-level) because the enriched build closes over
 * the CDF client + hierarchy/entity views to custom-fetch and resolve ancestry.
 */

import { describeAncestry, STRING_CONTEXT_ENRICH } from '../enrichAncestors.ts';
import { ESTIMATED_RATES_CONTEXT, METHOD_ESTIMATE, METHOD_METHOD, METHOD_STATUS } from '../shapes/index.ts';
import { makeStringContextViews } from './makeStringContextViews.ts';
import type { ViewFamilySpec } from './viewFamilySpec.ts';
import { EstimatedRatesStringAgentView } from '#schema/generated/ipso/views.ts';

/** StringContext: well/field/reservoir ancestry off the `wellString` ref. */

/** One analysis-method estimated-rates surface: a canonical name, agent doc, and the de-prefixed groups to keep. */
type EstimatedRatesSpec = ViewFamilySpec;

/**
 * The seven EstimatedRates subviews, declared as data. Each de-prefixes one
 * analysis method's estimated-rates measure family and keeps the un-prefixed
 * reservoir-pressure context once; all share identity + the wellString relation
 * and the well/field/reservoir ancestry enrichment.
 *
 * The verbatim method prefixes: `priority` is bare, the rest carry the
 * `Analysis` segment. Only system/choke carry `METHOD_STATUS`; only priority
 * carries `METHOD_METHOD`.
 */
const ESTIMATED_RATES_SPECS: readonly EstimatedRatesSpec[] = [
  {
    name: 'ChokeEstimatedRates',
    description: `Per-string choke-analysis estimated production rates (oil/gas/water/liquid rate at standard conditions, flowing bottom-hole pressure, analysis status) and reservoir-pressure context${describeAncestry(STRING_CONTEXT_ENRICH.relations)}`,
    groups: [
      { shape: METHOD_ESTIMATE, prefix: 'chokeAnalysis' },
      { shape: METHOD_STATUS, prefix: 'chokeAnalysis' },
      { shape: ESTIMATED_RATES_CONTEXT },
    ],
  },
  {
    name: 'CurveEstimatedRates',
    description: `Per-string curve-analysis estimated production rates (oil/gas/water/liquid rate at standard conditions, flowing bottom-hole pressure) and reservoir-pressure context${describeAncestry(STRING_CONTEXT_ENRICH.relations)}`,
    groups: [{ shape: METHOD_ESTIMATE, prefix: 'curveAnalysis' }, { shape: ESTIMATED_RATES_CONTEXT }],
  },
  {
    name: 'DataEstimatedRates',
    description: `Per-string data-analysis estimated production rates (oil/gas/water/liquid rate at standard conditions, flowing bottom-hole pressure) and reservoir-pressure context${describeAncestry(STRING_CONTEXT_ENRICH.relations)}`,
    groups: [{ shape: METHOD_ESTIMATE, prefix: 'dataAnalysis' }, { shape: ESTIMATED_RATES_CONTEXT }],
  },
  {
    name: 'IprEstimatedRates',
    description: `Per-string IPR-analysis estimated production rates (oil/gas/water/liquid rate at standard conditions, flowing bottom-hole pressure) and reservoir-pressure context${describeAncestry(STRING_CONTEXT_ENRICH.relations)}`,
    groups: [{ shape: METHOD_ESTIMATE, prefix: 'iprAnalysis' }, { shape: ESTIMATED_RATES_CONTEXT }],
  },
  {
    name: 'VlpEstimatedRates',
    description: `Per-string VLP-analysis estimated production rates (oil/gas/water/liquid rate at standard conditions, flowing bottom-hole pressure) and reservoir-pressure context${describeAncestry(STRING_CONTEXT_ENRICH.relations)}`,
    groups: [{ shape: METHOD_ESTIMATE, prefix: 'vlpAnalysis' }, { shape: ESTIMATED_RATES_CONTEXT }],
  },
  {
    name: 'SystemEstimatedRates',
    description: `Per-string system-analysis estimated production rates (oil/gas/water/liquid rate at standard conditions, flowing bottom-hole pressure, analysis status) and reservoir-pressure context${describeAncestry(STRING_CONTEXT_ENRICH.relations)}`,
    groups: [
      { shape: METHOD_ESTIMATE, prefix: 'systemAnalysis' },
      { shape: METHOD_STATUS, prefix: 'systemAnalysis' },
      { shape: ESTIMATED_RATES_CONTEXT },
    ],
  },
  {
    name: 'PriorityEstimatedRates',
    description: `Per-string priority estimated production rates (oil/gas/water/liquid rate at standard conditions, flowing bottom-hole pressure) plus the analysis method that produced them and reservoir-pressure context${describeAncestry(STRING_CONTEXT_ENRICH.relations)}`,
    groups: [
      { shape: METHOD_ESTIMATE, prefix: 'priority' },
      { shape: METHOD_METHOD, prefix: 'priority' },
      { shape: ESTIMATED_RATES_CONTEXT },
    ],
  },
];

/**
 * Build the seven per-method, ancestry-enriched EstimatedRates surfaces, each a
 * StringContext view over the shared source + well/field/reservoir enrichment;
 * filter by wellString (or join WellEntity) to scope by hierarchy.
 */
export const useEstimatedRatesViews = makeStringContextViews({
  source: () => EstimatedRatesStringAgentView,
  specs: ESTIMATED_RATES_SPECS,
});
